<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'NAVOnlineInvoiceInterfacePanel',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const items = ref(null);
  const selectedCompanies = ref({});
  const ux = ref([]);
  const b_3343864469950044_style = computed(() => ({
    width: '1px',
  }));
  const b_5477420070495986_style = computed(() => ({
    width: '1px',
    'vertical-align': 'middle',
  }));
  const b_6337614691287098_style = computed(() => ({
    width: '1px',
    'vertical-align': 'middle',
  }));
  const b_2406129194387753_style = computed(() => ({
    width: 'auto',
  }));

  const $data = computed(() => ({
    items: items.value,
    selectedCompanies: selectedCompanies.value,
    ux: ux.value,
    b_3343864469950044_style: b_3343864469950044_style.value,
    b_5477420070495986_style: b_5477420070495986_style.value,
    b_6337614691287098_style: b_6337614691287098_style.value,
    b_2406129194387753_style: b_2406129194387753_style.value,
  }));

  const $$load_items = () => {
    return $fn.value
      .fetch('list_nav_online_interface_setting')
      .then((data) => _.orderBy(data, ['company_name']));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'items' },
      {
        name: 'selectedCompanies',
        cache: {
          type: 'local',
          key: 'NAVOnlineInvoiceInterface.selectedCompanies',
        },
      },
      { name: 'ux' },
      { name: 'b_3343864469950044_style', nodeId: '3343864469950044' },
      { name: 'b_5477420070495986_style', nodeId: '5477420070495986' },
      { name: 'b_6337614691287098_style', nodeId: '6337614691287098' },
      { name: 'b_2406129194387753_style', nodeId: '2406129194387753' },
    ],
    loaders: { items: $$load_items },
    ux: {},
    dataRefs: {
      items,
      selectedCompanies,
      ux,
      b_3343864469950044_style,
      b_5477420070495986_style,
      b_6337614691287098_style,
      b_2406129194387753_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const selectedCompanyValues = computed(() => {
    if (loaded.value) {
      return _.keys(_.pickBy(selectedCompanies.value));
    }
    return null;
  });

  const act_5615156174146796_showModal_r1azhexmb_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'NAVOnlineInvoiceInterfaceSettings',
      props: {
        companyId: $event.scope.item.company_id,
      },
    };
  };

  const act_7315092382398562_reloadComponentData_z9edtalhv =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_z2om1lwxb =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_z9edtalhv,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_z2om1lwxb],
    event: 'reload',
  });
  const act_5615156174146796_reloadComponentData_ih6quhklu =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_5615156174146796_close_v36y409oc =
    new actionTypes.EventHandlerImpl({
      action: act_5615156174146796_reloadComponentData_ih6quhklu,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_5615156174146796_showModal_r1azhexmb =
    new actionTypes.ShowModalAction({
      actionArgs: act_5615156174146796_showModal_r1azhexmb_getActionArgs,
      events: [inst_evh_5615156174146796_close_v36y409oc],
    });
  const inst_evh_5615156174146796_click_uwlivumur =
    new actionTypes.EventHandlerImpl({
      action: act_5615156174146796_showModal_r1azhexmb,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_5615156174146796_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_5615156174146796_click_uwlivumur],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_z2om1lwxb,
    inst_evh_7315092382398562_reload,
    inst_evh_5615156174146796_close_v36y409oc,
    inst_evh_5615156174146796_click_uwlivumur,
    inst_evh_5615156174146796_click,
  };

  const evh_7315092382398562_reload_z2om1lwxb = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_z2om1lwxb.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5615156174146796_close_v36y409oc = async (event, scope) => {
    return await actions.inst_evh_5615156174146796_close_v36y409oc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5615156174146796_click_uwlivumur = async (event, scope) => {
    return await actions.inst_evh_5615156174146796_click_uwlivumur.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_5615156174146796_click = async (event, scope) => {
    return await actions.inst_evh_5615156174146796_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div class="card my-3">
      <div class="card-body">
        <h5>
          {{ gettext('NAV online invoice interface') }}
        </h5>
        <p class="card-text">
          {{
            pgettext(
              'Tasks',
              'Import invoices from NAV online invoices service.'
            )
          }}
        </p>
      </div>
      <table class="table mb-0 mx-3 w-auto" :style="b_3343864469950044_style">
        <tbody>
          <tr :key="item.company_id" v-for="item in items">
            <td :style="b_5477420070495986_style">
              <BFormCheckbox
                :disabled="item.not_configured"
                v-model="selectedCompanies[item.company_id]"
              >
                <ItpText strong :text="item.company_name"> </ItpText>
              </BFormCheckbox>
            </td>
            <td :style="b_6337614691287098_style">
              <ItpBadge
                :text="pgettext('NAVOnlineInvoiceInterface', 'Not configured')"
                variant="warning"
                v-if="item.not_configured"
              >
              </ItpBadge>
            </td>
            <td :style="b_2406129194387753_style">
              <ItpButton
                icon="mdi:cog"
                :text="pgettext('Tasks|Command', 'Configure')"
                @click="evh_5615156174146796_click($event, { item })"
              >
              </ItpButton>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="card-footer bg-transparent">
        <JobHandlerControl
          jobType="invoices:nav:pull-invoices"
          load-current
          :disabled="!selectedCompanyValues || !selectedCompanyValues.length"
          :payload="() => ({ companies: selectedCompanyValues })"
          :text="
            pgettext('Tasks|Command', 'Pull new invoices from NAV services.')
          "
          :icon="$config.pullDataIcon"
        >
        </JobHandlerControl>
      </div>
    </div>
  </ItpUsercontrol>
</template>
